import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Loader2 } from 'lucide-react';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { useTenant } from '@/hooks/use-tenant';
import {
  PREDEFINED_RANGE_VALUES,
  useMetricsDateFilter,
} from '@/hooks/use-metrics-date-filter';
import { MetricsFilterBar } from '@/components/metrics-filter-bar';
import { AnalyticsTabsBar } from '@/components/analytics/analytics-tabs-bar';
import { OverviewTab } from '@/components/analytics/overview-tab';
import {
  CampaignsTabPanel,
  SalesTabPanel,
  UsersTabPanel,
  type AnalyticsTabPanelProps,
} from '@/components/analytics/analytics-tab-panels';
import { AnalyticsOrdersTab } from '@/components/sales-history/analytics-orders-tab';
import { OrdersHeaderMenu } from '@/components/sales-history/orders-header-menu';
import { useSalesHistoryFilters } from '@/components/sales-history/use-sales-history-filters';
import { useAnalyticsTab } from '@/hooks/use-analytics-tab';
import { useIsMobile } from '@/hooks/use-mobile';
import { MockedMetrics } from '@/components/mocked-metrics';
import { AnalyticsDisabledOverlay } from '@/components/metrics/analytics-disabled-overlay';
import { HelpPanelProvider } from '@/components/help-panel/help-panel-context';
import { HelpPanel } from '@/components/help-panel/help-panel';
import { MetricsPageTour } from '@/hooks/use-metrics-page-tour';
import { useOrdersDrilldownState } from '@/hooks/use-orders-drilldown-state';
import { buildDrilldownQuery } from '@/lib/orders-drilldown';
import { OrdersDrilldown } from '@/components/orders-drilldown/orders-drilldown';
import { useFeatureFlags } from '@/hooks/use-feature-flags';

export default function MetricsPage() {
  const tenant = useTenant();
  const { LL } = useI18nContext();
  const filter = useMetricsDateFilter();
  const { fromDate, toDate, granularity, dateFilterLabel } = filter;

  const featureFlags = useFeatureFlags();

  const analyticsEnabled = featureFlags.data?.analyticsEnabled ?? false;

  const drilldown = useOrdersDrilldownState();
  const drilldownQuery =
    analyticsEnabled && fromDate && toDate
      ? buildDrilldownQuery(drilldown.state, { fromDate, toDate, granularity })
      : null;
  const openDrilldown = analyticsEnabled ? drilldown.open : undefined;

  const isMobile = useIsMobile();
  const { tab, setTab } = useAnalyticsTab();
  // Pedidos keeps its own filters and period here, so they survive switching
  // tabs (SALES-07, Assumption "Período da aba Pedidos").
  const salesHistoryFilters = useSalesHistoryFilters();
  const isOrdersTab = tab === 'orders';
  const showMockedMetrics =
    featureFlags.data !== undefined && !featureFlags.data.analyticsEnabled;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  // Charts tapped on mobile scroll into view below the sticky block.
  useChartAnchorOffset(scrollContainerRef, stickyRef, !featureFlags.isLoading);

  // Only the open tab is mounted, so only its queries run (ANLT-01, AD-038).
  const panelProps: AnalyticsTabPanelProps = {
    fromDate,
    toDate,
    granularity,
    preset: PREDEFINED_RANGE_VALUES.find(
      (value) => value === filter.predefinedDateRange
    ),
    enabled: analyticsEnabled,
    dateFilterLabel,
    openDrilldown,
  };
  const metricsPanel =
    tab === 'sales' ? (
      <SalesTabPanel {...panelProps} />
    ) : tab === 'campaigns' ? (
      <CampaignsTabPanel {...panelProps} />
    ) : tab === 'users' ? (
      <UsersTabPanel {...panelProps} />
    ) : (
      <OverviewTab {...panelProps} onOpenTab={setTab} />
    );

  return (
    <SidebarLayout
      className="pt-0 md:pt-0 md:p-0 p-0 overflow-hidden"
      mobileHeader={{
        title: LL.metrics(),
        actions: isOrdersTab ? (
          <OrdersHeaderMenu filters={salesHistoryFilters} />
        ) : undefined,
      }}
    >
      {featureFlags.isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <HelpPanelProvider>
          {/* The tour is desktop-only (SALES-04 AC 6). */}
          {!isMobile && <MetricsPageTour />}
          <div className="flex h-full min-h-0 w-full flex-1 items-stretch">
            <div
              ref={scrollContainerRef}
              className="relative min-h-0 min-w-0 flex-1 overflow-y-auto p-8 md:p-6 pt-0 md:pt-0"
            >
              <MetricsFilterBar
                title={LL.metrics()}
                filter={filter}
                showGranularity
                hidePeriod={isOrdersTab}
                hideTitleOnMobile
                stickyRef={stickyRef}
                tabs={<AnalyticsTabsBar tab={tab} onTabChange={setTab} />}
              />

              {isOrdersTab ? (
                <AnalyticsOrdersTab filters={salesHistoryFilters} />
              ) : (
                <div className="relative">
                  {showMockedMetrics && (
                    <AnalyticsDisabledOverlay scope="panel" />
                  )}
                  {showMockedMetrics && <MockedMetrics />}
                  <div data-analytics-panel={tab}>{metricsPanel}</div>
                </div>
              )}

              <OrdersDrilldown
                tenantSlug={tenant.slug}
                query={drilldownQuery}
                origin={drilldownQuery === null ? null : drilldown.state.origin}
                granularity={granularity}
                periodLabel={dateFilterLabel}
                onPageChange={drilldown.setPage}
                onSortChange={drilldown.setSort}
                onSegmentChange={drilldown.setSegment}
                onViewChange={drilldown.setView}
                onSearchChange={drilldown.setSearch}
                onClose={drilldown.close}
              />
            </div>
            <HelpPanel />
          </div>
        </HelpPanelProvider>
      )}
    </SidebarLayout>
  );
}

/**
 * Keeps `--chart-anchor-offset` on the scroll container equal to the sticky
 * block's height (charts use it as `scroll-margin-top`).
 */
function useChartAnchorOffset(
  containerRef: RefObject<HTMLElement | null>,
  stickyRef: RefObject<HTMLElement | null>,
  mounted: boolean
): void {
  useLayoutEffect(() => {
    const container = containerRef.current;
    const sticky = stickyRef.current;
    if (!mounted || !container || !sticky) return;
    const update = () =>
      container.style.setProperty(
        '--chart-anchor-offset',
        `${sticky.getBoundingClientRect().height}px`
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(sticky);
    return () => observer.disconnect();
  }, [containerRef, stickyRef, mounted]);
}
